import clsx from 'clsx'
import React, { useCallback, useState } from 'react'
import { useControlField, useField } from 'remix-validated-form'
import { Checkbox, Label } from '~/components/ui'
import { cn } from '~/utils'

type CheckboxFieldProps = {
  className?: string
  label: string
  name: string
  disabled?: boolean
  defaultValue?: boolean
  onBlur?: () => void
  onCheckedChange?: (value: string | boolean) => void
}

const CheckboxField = React.memo(function CheckboxField(
  props: CheckboxFieldProps
) {
  const {
    className,
    name,
    label,
    disabled,
    defaultValue,
    onBlur: customOnBlur,
    onCheckedChange,
  } = props
  const { error, getInputProps, defaultValue: previousValue } = useField(name)
  const { onBlur } = getInputProps()
  const [value, setValue] = useControlField(name)
  const [hasInteracted, setHasInteracted] = useState(false)
  const checkedValue = typeof value !== 'undefined' ? !!value : !!defaultValue

  const handleCheckedChange = useCallback(
    (value: string | boolean) => {
      if (disabled) {
        return
      }
      if (!hasInteracted) {
        setHasInteracted(true)
      }
      setValue(value.valueOf())
      onCheckedChange?.(value.valueOf())
      customOnBlur?.()
      onBlur?.()
    },
    [setValue, onCheckedChange, customOnBlur, onBlur, hasInteracted, disabled]
  )

  const checkboxClassName = React.useMemo(() => {
    return clsx(
      hasInteracted &&
        previousValue &&
        value != previousValue &&
        'text-yellow-300 ring-2 ring-yellow-300 ring-offset-2',
      disabled && 'cursor-not-allowed opacity-50'
      // ((!previousValue && value === true) || value === 'on') &&
      //   'text-green-400 ring-2 ring-offset-2 ring-green-400'
    )
  }, [previousValue, value, hasInteracted, disabled])

  return (
    <div className={cn('flex items-center space-x-2', className)}>
      <input
        checked={checkedValue}
        className="sr-only"
        name={name}
        readOnly
        tabIndex={-1}
        type="checkbox"
      />
      <Checkbox
        id={name}
        aria-disabled={disabled || undefined}
        checked={checkedValue}
        onCheckedChange={handleCheckedChange}
        tabIndex={disabled ? -1 : undefined}
        className={checkboxClassName}
      />
      <Label htmlFor={name} className="font-normal leading-tight">
        {label}
      </Label>
    </div>
  )
})

export default CheckboxField
